import { useEffect, useRef, useState } from 'react';
import {
  Button,
  CheckCircleIcon,
  Checkbox,
  ConfirmationDialog,
  confirmDialog,
  InputText,
  useGlobalToast,
} from '@repo/atomic-components';
import {
  useCloneChatToBenchmarkingMutation,
  useCloneMatterToBenchmarkingMutation,
  useLazyGetMatterChatsQuery,
  type CaseChatsToClone,
  type ChatHistoryData,
} from '@/api';
import { isRtkFetchErrorWithStatus } from '@/api/utils';
import { selectUserAboutData, useAppSelector } from '@/store';
import { formatDate } from '@/utils/formatDate';
import { SegmentedControl } from '../SegmentedControl';
import type { SettingControlProps } from '../types';
import { parseCloneId, type CloneKind } from './parseCloneIds';

/** Shows the case clone flow alongside the chat one. */
export const CAN_CLONE_CASES = true;

/** Clones always land in this organisation, whichever one the admin is in. */
const BENCHMARKS_ORG_NAME = 'Benchmarks';

export const CLONE_CONFIRM_DIALOG_ID = 'benchmarking-clone-confirm';
const CLONE_CONFIRM_TITLE_ID = 'benchmarking-clone-confirm-title';

const KIND_OPTIONS = [
  { value: 'chat', label: 'Chats' },
  { value: 'case', label: 'Cases' },
] as const;

const CASE_CHATS_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'SELECTED', label: 'Selected' },
  { value: 'ALL', label: 'All' },
] as const;

const countOf = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** "chat 1a2b…", or "case 1a2b…" followed by what happens to its chats. */
const describeClone = (kind: CloneKind, id: string, caseChats: CaseChatsToClone, chatIds: string[]) => {
  if (kind === 'chat') return `chat ${id}`;

  const withChats = {
    NONE: 'with no chats',
    SELECTED: `with ${countOf(chatIds.length, 'selected chat')}`,
    ALL: 'with all its chats',
  }[caseChats];

  return `case ${id} ${withChats}`;
};

/** Same cap the case history page uses. */
const MATTER_CHATS_LIMIT = 500;

/** `chatIds` are the clones of a case's chats. */
type ClonedPair = { sourceId: string; cloneId: string; chatIds?: string[] };

/** A case that exists, with the chats it can bring along. */
type CheckedCase = { id: string; chats: ChatHistoryData[] };

/** Long enough to read, or copy, the new id. */
const CLONE_TOAST_LIFE_MS = 10 * 1000;

/** The backend's own message when it sent one, e.g. for a chat that doesn't exist. */
const readErrorDetail = (error: unknown) => {
  const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
  return typeof detail === 'string' ? detail : null;
};

const ID_INPUT_CLASS_NAME =
  '!border-theme-border-grey !text-theme-black !bg-theme-white w-full max-w-120 !rounded-lg border font-mono !text-sm';

const SECONDARY_BUTTON_CLASS_NAME =
  '!text-theme-black !border-theme-border-grey !bg-theme-white hover:bg-theme-background-grey! flex h-8 w-fit justify-center px-4';

type IdFieldProps = {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  /** Freezes the field and shows a green tick with this text as its label. */
  confirmedLabel?: string;
};

const IdField = ({ id, label, hint, value, onChange, confirmedLabel }: IdFieldProps) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className="text-theme-black text-sm font-semibold">
      {label}
    </label>
    <span id={`${id}-hint`} className="text-theme-text-grey text-xs">
      {hint}
    </span>
    <div className="flex items-center gap-2">
      <InputText
        id={id}
        aria-describedby={`${id}-hint`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        readOnly={Boolean(confirmedLabel)}
        spellCheck={false}
        className={`${ID_INPUT_CLASS_NAME} h-8`}
      />
      {confirmedLabel && (
        <CheckCircleIcon
          role="img"
          aria-label={confirmedLabel}
          className="text-theme-dark-green shrink-0 [&_path]:fill-current"
        />
      )}
    </div>
  </div>
);

type BenchmarkingCloneControlProps = SettingControlProps & {
  /** Shows the Chats / Cases picker. Defaults to `CAN_CLONE_CASES`. */
  canCloneCases?: boolean;
};

/** Global-admin tool that copies a chat or a case into the Benchmarks organisation, after a second confirmation. */
export const BenchmarkingCloneControl = ({
  labelId,
  canCloneCases = CAN_CLONE_CASES,
}: BenchmarkingCloneControlProps) => {
  const toastRef = useGlobalToast();
  const userAboutData = useAppSelector(selectUserAboutData);
  const [cloneChat, { isLoading: isCloningChat }] = useCloneChatToBenchmarkingMutation();
  const [cloneMatter, { isLoading: isCloningMatter }] = useCloneMatterToBenchmarkingMutation();
  const [getMatterChats] = useLazyGetMatterChatsQuery();

  const [kind, setKind] = useState<CloneKind>('chat');
  const [chatIdText, setChatIdText] = useState('');
  const [caseIdText, setCaseIdText] = useState('');
  const [checkedCase, setCheckedCase] = useState<CheckedCase | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [caseChats, setCaseChats] = useState<CaseChatsToClone>('ALL');
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cloned, setCloned] = useState<{ orgName: string; pair: ClonedPair } | null>(null);

  // Bumped whenever the case step is reset, so a check that finishes afterwards is ignored.
  const checkRunRef = useRef(0);

  const isCloning = isCloningChat || isCloningMatter;
  const canClone =
    kind === 'chat'
      ? chatIdText.trim() !== ''
      : checkedCase !== null && (caseChats !== 'SELECTED' || selectedChatIds.length > 0);

  const kindLabelId = `${labelId}-kind`;
  const idsInputId = `${labelId}-ids`;
  const caseChatsLabelId = `${labelId}-case-chats`;
  const errorId = `${labelId}-error`;
  const checkButtonId = `${labelId}-check`;
  const cloneButtonId = `${labelId}-clone`;

  // The popup doesn't hand focus back, and the button is disabled while the clone runs, so return
  // focus once it is enabled again.
  const wasCloningRef = useRef(false);
  useEffect(() => {
    if (wasCloningRef.current && !isCloning && document.activeElement === document.body) {
      document.getElementById(cloneButtonId)?.focus();
    }
    wasCloningRef.current = isCloning;
  }, [isCloning, cloneButtonId]);

  /** Back to the case ID step, with nothing chosen. */
  const resetCaseChecks = () => {
    checkRunRef.current += 1;
    setCheckedCase(null);
    setCaseChats('ALL');
    setSelectedChatIds([]);
    setError(null);
  };

  const handleKindChange = (nextKind: CloneKind) => {
    setKind(nextKind);
    setCaseIdText('');
    resetCaseChecks();
  };

  const handleChangeCase = () => {
    resetCaseChecks();
    document.getElementById(idsInputId)?.focus();
  };

  const handleChatToggle = (chatId: string, isSelected: boolean) =>
    setSelectedChatIds((current) => (isSelected ? [...current, chatId] : current.filter((id) => id !== chatId)));

  const handleCheckCase = async () => {
    const caseId = parseCloneId(caseIdText, 'case');
    if (!caseId) return setError("That isn't a case ID or a Fletch case link.");

    setError(null);
    setIsChecking(true);
    const run = ++checkRunRef.current;

    try {
      const { chats } = await getMatterChats({ matterId: caseId, limit: MATTER_CHATS_LIMIT }).unwrap();
      if (run === checkRunRef.current) setCheckedCase({ id: caseId, chats });
    } catch (error) {
      if (run !== checkRunRef.current) return;

      const status = isRtkFetchErrorWithStatus(error) ? error.status : null;

      if (status === 404) {
        setError("We couldn't find that case. Check the ID or link and try again.");
      } else if (status === 403) {
        setError(
          "That case isn't in your current organisation. Switch to the organisation it belongs to (Settings › Organisation), then check it again.",
        );
      } else {
        console.error('Case check failed:', error);
        setError("Couldn't check the case. Please try again.");
      }
    } finally {
      setIsChecking(false);
    }
  };

  /** Asks for the second confirmation, then runs `clone` and lists what it made. */
  const confirmAndClone = async (
    { details, orgName, chatIds }: { details: string; orgName: string; chatIds: string[] },
    clone: () => Promise<ClonedPair>,
  ) => {
    setError(null);
    const confirmed = await confirmDialog(
      { id: CLONE_CONFIRM_DIALOG_ID, details, orgName, chatIds },
      { forceClosePrev: true },
    );
    if (!confirmed) {
      document.getElementById(cloneButtonId)?.focus();
      return;
    }

    try {
      const pair = await clone();
      setCloned({ orgName, pair });
      setChatIdText('');
      setCaseIdText('');
      resetCaseChecks();
      // The button stays disabled until there is an id again, so focus can't go back to it.
      document.getElementById(idsInputId)?.focus();
      toastRef.current?.show({
        closable: true,
        severity: 'success',
        summary: `Cloned into ${orgName}`,
        detail: `New ${kind} ID: ${pair.cloneId}`,
        life: CLONE_TOAST_LIFE_MS,
      });
    } catch (error) {
      console.error('Benchmarking clone failed:', error);
      toastRef.current?.show({
        closable: true,
        severity: 'error',
        summary: `Couldn't clone into ${orgName}`,
        detail: readErrorDetail(error) ?? 'Please try again.',
        life: CLONE_TOAST_LIFE_MS,
      });
    }
  };

  const handleCloneChat = () => {
    const chatId = parseCloneId(chatIdText, 'chat');
    if (!chatId) return setError("That isn't a chat ID or a Fletch chat link.");

    return confirmAndClone(
      { details: describeClone('chat', chatId, caseChats, []), orgName: BENCHMARKS_ORG_NAME, chatIds: [] },
      async () => {
        const clone = await cloneChat({ chatIdToClone: chatId, globalAdminAsUser: userAboutData?.userId }).unwrap();
        return { sourceId: chatId, cloneId: clone.newChatId };
      },
    );
  };

  const handleCloneCase = () => {
    if (!checkedCase) return;

    const chatIds = caseChats === 'SELECTED' ? selectedChatIds : [];
    return confirmAndClone(
      { details: describeClone('case', checkedCase.id, caseChats, chatIds), orgName: BENCHMARKS_ORG_NAME, chatIds },
      async () => {
        const clone = await cloneMatter({
          matterIdToClone: checkedCase.id,
          matterChatIdsToClone: caseChats === 'ALL' ? undefined : chatIds,
        }).unwrap();
        return { sourceId: checkedCase.id, cloneId: clone.newMatterId, chatIds: clone.newMatterChatIds };
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* With chats the only choice, there is nothing to pick. */}
      {canCloneCases && (
        <div className="flex flex-col gap-1">
          <span id={kindLabelId} className="text-theme-black text-sm font-semibold">
            What to clone
          </span>
          <SegmentedControl labelId={kindLabelId} options={KIND_OPTIONS} value={kind} onChange={handleKindChange} />
        </div>
      )}

      {kind === 'chat' ? (
        <IdField
          id={idsInputId}
          label="Chat ID"
          hint="A Fletch chat link works too."
          value={chatIdText}
          onChange={setChatIdText}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <IdField
            id={idsInputId}
            label="Case ID"
            hint={
              checkedCase
                ? `Found, with ${countOf(checkedCase.chats.length, 'chat')}.`
                : 'You must be in the organisation you want to clone the case from. A Fletch case link works too.'
            }
            value={caseIdText}
            onChange={setCaseIdText}
            confirmedLabel={checkedCase ? 'Case found' : undefined}
          />
          {checkedCase ? (
            <Button
              type="button"
              outlined
              size="small"
              onClick={handleChangeCase}
              disabled={isChecking}
              className={SECONDARY_BUTTON_CLASS_NAME}
            >
              Change
            </Button>
          ) : (
            <Button
              id={checkButtonId}
              type="button"
              outlined
              size="small"
              onClick={handleCheckCase}
              disabled={isChecking || caseIdText.trim() === ''}
              loading={isChecking}
              aria-describedby={error ? errorId : undefined}
              className={SECONDARY_BUTTON_CLASS_NAME}
            >
              Check
            </Button>
          )}
        </div>
      )}

      {checkedCase &&
        (checkedCase.chats.length === 0 ? (
          <p className="text-theme-text-grey text-sm">This case has no chats.</p>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <span id={caseChatsLabelId} className="text-theme-black text-sm font-semibold">
                Chats to include
              </span>
              <SegmentedControl
                labelId={caseChatsLabelId}
                options={CASE_CHATS_OPTIONS}
                value={caseChats}
                onChange={setCaseChats}
              />
            </div>

            {caseChats === 'SELECTED' && (
              <div
                role="group"
                aria-label="Chats to clone"
                className="flex max-h-60 flex-col gap-3 overflow-y-auto p-1"
              >
                {checkedCase.chats.map((chat) => (
                  <label key={chat.chatId} className="flex cursor-pointer items-start gap-2">
                    <Checkbox
                      type="checkbox"
                      checked={selectedChatIds.includes(chat.chatId)}
                      onChange={(event) => handleChatToggle(chat.chatId, !!event.target.checked)}
                      className="case-list-filter-checkbox bg-theme-white !text-theme-black !border-theme-text-grey"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="text-theme-black text-sm break-words">{chat.title}</span>
                      <span className="text-theme-text-grey font-mono text-xs break-all">{chat.chatId}</span>
                      <span className="text-theme-text-grey text-xs">
                        {chat.user} · {formatDate(chat.lastUpdated)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </>
        ))}

      <p className="text-theme-text-grey text-sm">
        The {kind} will be cloned into the <span className="text-theme-black font-semibold">{BENCHMARKS_ORG_NAME}</span>{' '}
        organisation.
      </p>

      {error && (
        <p id={errorId} role="alert" className="text-theme-text-red text-sm">
          {error}
        </p>
      )}

      {(kind === 'chat' || checkedCase) && (
        <Button
          id={cloneButtonId}
          type="button"
          onClick={kind === 'chat' ? handleCloneChat : handleCloneCase}
          disabled={isCloning || isChecking || !canClone}
          loading={isCloning}
          aria-describedby={error ? errorId : undefined}
          className="bg-theme-dark-green hover:bg-theme-dark-green text-theme-white flex h-8 w-fit items-center justify-center rounded-[4px] px-4 text-sm font-semibold disabled:opacity-50"
        >
          Clone
        </Button>
      )}

      {cloned && (
        <div className="flex flex-col gap-1">
          <span className="text-theme-black text-sm font-semibold">Cloned into {cloned.orgName}</span>
          <ul className="text-theme-text-grey flex flex-col gap-0.5 font-mono text-xs">
            <li className="break-all">
              {cloned.pair.sourceId} → <span className="text-theme-black">{cloned.pair.cloneId}</span>
              {cloned.pair.chatIds && cloned.pair.chatIds.length > 0 && (
                <ul className="flex flex-col gap-0.5 pl-4">
                  {cloned.pair.chatIds.map((chatId) => (
                    <li key={chatId}>
                      chat <span className="text-theme-black">{chatId}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>
        </div>
      )}

      <CloneConfirmationDialog />
    </div>
  );
};

const CloneConfirmationDialog = () => (
  <ConfirmationDialog
    id={CLONE_CONFIRM_DIALOG_ID}
    style={{ width: 'min(480px, calc(100vw - 32px))' }}
    contentClassName="!px-6 !pt-5 rounded-lg bg-theme-white"
    pt={{ root: { 'aria-labelledby': CLONE_CONFIRM_TITLE_ID }, header: { className: '!hidden' } }}
  >
    {(params, { accept, reject }) => {
      const chatIds = params.chatIds as string[];

      return (
        <div className="flex flex-col gap-4">
          <h2 id={CLONE_CONFIRM_TITLE_ID} className="text-theme-black text-base font-semibold">
            Clone into {String(params.orgName)}?
          </h2>
          <p className="text-theme-black text-sm">
            Are you sure you want to clone <span className="font-semibold">{String(params.details)}</span> into{' '}
            <span className="font-semibold">{String(params.orgName)}</span>?
          </p>
          {chatIds.length > 0 && (
            <ul className="bg-theme-background-grey text-theme-black max-h-[160px] overflow-y-auto rounded-md px-3 py-2 font-mono text-xs break-all">
              <li className="text-theme-text-grey font-sans">Chats to include:</li>
              {chatIds.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          )}
          <div className="flex justify-end gap-3">
            <Button
              outlined
              onClick={reject}
              // The header and its close button are hidden, so nothing else would take focus.
              autoFocus
              size="small"
              className="!text-theme-black !border-theme-border-grey !bg-theme-white hover:bg-theme-background-grey! flex h-8 justify-center px-4"
            >
              Cancel
            </Button>
            <Button
              onClick={accept}
              size="small"
              className="bg-theme-dark-green hover:bg-theme-dark-green text-theme-white flex h-8 justify-center px-6"
            >
              Yes
            </Button>
          </div>
        </div>
      );
    }}
  </ConfirmationDialog>
);
